/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { inject, Injectable } from '@angular/core';
import { type CanDeactivateFn } from '@angular/router';
import { EventService, I18nService, PromptService } from '@dm/core';
import { Observable } from 'rxjs';

export interface CanLeave {
    selfLeaveCheck?: () => Promise<boolean>;
    canLeave: () => boolean;
}

export abstract class CanLeaveToken implements CanLeave {
    abstract canLeave(): boolean;
}

@Injectable({ providedIn: 'root' })
class CanLeaveGuardClass {
    constructor(
        private promptService: PromptService,
        private i18n: I18nService,
        private eventService: EventService,
    ) { }

    canDeactivate(component: CanLeave): Observable<boolean> | boolean {
        return Observable.create((observer) => {
            const selfLeaveCheck = component.selfLeaveCheck?.();

            if (Object.prototype.toString.call(selfLeaveCheck) === '[object Promise]') {
                selfLeaveCheck.then((isLeave: boolean) => {
                    observer.next(isLeave);
                }).catch(() => {
                    observer.next(true);
                }).finally(() => {
                    observer.complete();
                });

                return;
            }

            if (component.canLeave?.()) {
                observer.next(true);
                observer.complete();
                return;
            }
            this.promptService.promptConfirm({
                title: this.i18n.get('common.leavePage_title'),
                content: this.i18n.get('common.cancleCurrentPage_leavePage_label'),
                onCancel: (() => {
                    this.promptService.close();
                    observer.next(false);
                    observer.complete();
                    this.eventService.emit('cancelNavigate');
                }),
                onOk: (() => {
                    this.promptService.close();
                    observer.next(true);
                    observer.complete();
                }),
            });
        });
    }
}

export const CanLeaveGuard: CanDeactivateFn<CanLeave> = (component: CanLeave): Observable<boolean> | boolean =>
    inject(CanLeaveGuardClass).canDeactivate(component);
